import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getLetterToHeavenDetail, getFlowerImages, deleteLetterToHeaven } from '../api';

import '../styles/LetterToHeavenPostDetailsMobile.css';
import FamilyPlazaTopSection from './family-plaza/FamilyPlazaTopSection';
import FamilyPlazaSubHeading from './family-plaza/FamilyPlazaSubHeading';

const LetterToHeavenPostDetailsMobile = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPasswordCorrect, setIsPasswordCorrect] = useState(false);
  const [enteredPassword, setEnteredPassword] = useState('');
  const [letterData, setLetterData] = useState(null);
  const [storedPassword, setStoredPassword] = useState('');

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Load letter data from API and get password from localStorage
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch letter details from API
        const [detailRes, imagesRes] = await Promise.all([
          getLetterToHeavenDetail(id),
          getFlowerImages(),
        ]);

        const detail = detailRes.data || {};
        const images = Array.isArray(imagesRes.data) ? imagesRes.data : [];
        const imageMap = {};
        images.forEach(img => { imageMap[img.boardStampId] = img.imageUrl; });

        // Get password, recipient, sender, selectedStamp from localStorage
        const localData = localStorage.getItem('letterToHeavenDataPC');
        const unifiedData = localStorage.getItem('letterToHeavenData');
        let localRecipient = '';
        let password = '';
        let localSender = '';
        let localSelectedStamp = '';

        // Try unified storage first, then platform-specific storage
        let dataToCheck = null;
        if (unifiedData) {
          try {
            dataToCheck = JSON.parse(unifiedData);
          } catch (e) {
            // Ignore parsing errors
          }
        }

        if (!dataToCheck && localData) {
          try {
            dataToCheck = JSON.parse(localData);
          } catch (e) {
            // Ignore parsing errors
          }
        }

        if (dataToCheck) {
          // Handle both array and single object formats
          const letters = Array.isArray(dataToCheck) ? dataToCheck : [dataToCheck];

          // Try to find by ID first
          let matchingLetter = letters.find(l => l.id == id);

          // If not found by ID, try to find by title and sender (for old posts)
          if (!matchingLetter && detail.title && detail.userName) {
            matchingLetter = letters.find(l =>
              l.title === detail.title &&
              l.sender === detail.userName
            );
          }

          // If still not found, try by title only
          if (!matchingLetter && detail.title) {
            matchingLetter = letters.find(l => l.title === detail.title);
          }

          if (matchingLetter) {
            localRecipient = matchingLetter.recipient || '';
            password = matchingLetter.password || '';
            localSender = matchingLetter.sender || '';
            localSelectedStamp = matchingLetter.selectedStamp || '';
            setStoredPassword(password);
          }
        }

        // Get recipient from board list if available
        const boardListRecipient = localStorage.getItem('selectedLetterRecipient') || '';

        const normalized = {
          id: detail.id,
          recipient: localRecipient || detail.toName || detail.recipient || detail.to || detail.to_name || detail.recipientName || detail.recipient_name || boardListRecipient || '',
          sender: localSender || detail.userName || detail.sender || '',
          title: detail.title,
          content: detail.content || '',
          date: detail.registDate || detail.date,
          selectedStamp: localSelectedStamp || ((typeof detail.boardStamp !== 'undefined') ? `stamp-${detail.boardStamp}` : (detail.selectedStamp || detail.flowerImageUrl || 'stamp-1')),
        };
        setLetterData(normalized);
      } catch (err) {
        console.error('Error fetching letter details:', err);
        setLetterData(null);
      }
    };
    if (id) fetchData();
  }, [id]);

  // Check password whenever enteredPassword changes
  useEffect(() => {
    if (storedPassword && enteredPassword) {
      setIsPasswordCorrect(enteredPassword === storedPassword);
    } else {
      setIsPasswordCorrect(false);
    }
  }, [enteredPassword, storedPassword]);

  // Handle password input change
  const handlePasswordChange = (password) => {
    setEnteredPassword(password);
  };

  const handleLetterToHeaven = () => {
    navigate('/letter-to-heaven-board-list');
  };

  const handleListButton = () => {
    navigate('/letter-to-heaven-board-list');
  };

  const handleModifyButton = () => {
    if (isPasswordCorrect && letterData) {
      // Save the current letter data for editing, include password so writing page pre-fills it
      const editingPayload = { ...letterData, password: storedPassword };
      localStorage.setItem('editingLetterToHeavenData', JSON.stringify(editingPayload));
      navigate('/letter-to-heaven-writing');
    }
  };

  const handleDeleteButton = async () => {
    if (isPasswordCorrect && letterData) {
      try {
        // Delete from API with password
        await deleteLetterToHeaven(letterData.id, enteredPassword);
        navigate('/letter-to-heaven-board-list');
      } catch (error) {
        console.error('Error deleting letter:', error);
        // Fallback to localStorage removal if API fails
        localStorage.removeItem('letterToHeavenDataPC');
        localStorage.removeItem('selectedLetterToHeavenData');
        localStorage.removeItem('editingLetterToHeavenDataPC');
        navigate('/letter-to-heaven-board-list');
      }
    }
  };

  const handleAncestralTablet = () => {
    navigate('/ancestral-tablet');
  };

  // Format date to YYYY.MM.DD format
  const formatDate = (dateString) => {
    if (!dateString) return '';

    try {
      // If already in YYYY.MM.DD format, return as is
      if (/^\d{4}\.\d{2}\.\d{2}$/.test(dateString)) {
        return dateString;
      }

      // If in YYYY-MM-DD format, convert to YYYY.MM.DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString.replace(/-/g, '.');
      }

      // For other formats, try to parse and format
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    } catch (error) {
      return dateString;
    }
  };

  const normalizeContent = (str) => {
    if (!str) return '';
    return str
      .replace(/&nbsp;/g, ' ')
      .replace(/\.{3}/g, '…');
  };



  return (

    <div className="letter-to-heaven-board-list-mobile-unique announcement-list-page donate-info-page-pc">
      <FamilyPlazaTopSection />
      <main className="main-content">
        <FamilyPlazaSubHeading heading="하늘로 보내는 편지" />

        {/* Navigation Header Styles - CEO Style */}
        {/* <div className="letter-to-heaven-post-details-mobile-dropdown-section-unique">
        <div className="letter-to-heaven-post-details-mobile-dropdown-container-unique">
          <div className="letter-to-heaven-post-details-mobile-dropdown-header-unique">
            <div className="letter-to-heaven-post-details-mobile-title-wrapper-unique">
              <div className="letter-to-heaven-post-details-mobile-title-decorator-unique"></div>
              <h2 className="letter-to-heaven-post-details-mobile-section-title-unique">하늘로 보내는 편지</h2>
            </div>
            <div 
              className="letter-to-heaven-post-details-mobile-more-icon-container-unique"
              onClick={toggleDropdown}
            >
              <img
                src="/arrows/up.png"
                alt="dropdown toggle"
                className={`letter-to-heaven-post-details-mobile-more-icon-unique ${
                  isDropdownOpen ? '' : 'letter-to-heaven-post-details-mobile-more-icon-rotated-unique'
                }`}
              />
            </div>
          </div>

       
          {isDropdownOpen && (
            <div className="letter-to-heaven-post-details-mobile-dropdown-unique">
              <div className="letter-to-heaven-post-details-mobile-dropdown-menu-unique">
                <div 
                  className="letter-to-heaven-post-details-mobile-nav-item-unique"
                  onClick={() => navigate('/cyber-memorial-search')}
                >
                  <span>사이버추모관</span>
                  <img 
                    src="/arrows/right.png" 
                    alt="arrow" 
                    className="letter-to-heaven-post-details-mobile-nav-arrow-unique" 
                  />
                </div>
                <div 
                  className="letter-to-heaven-post-details-mobile-nav-item-unique letter-to-heaven-post-details-mobile-active-unique"
                  onClick={handleLetterToHeaven}
                >
                  <span>하늘로 보내는 편지</span>
                  <img 
                    src="/arrows/right.png" 
                    alt="arrow" 
                    className="letter-to-heaven-post-details-mobile-nav-arrow-unique" 
                  />
                </div>
                                 <div 
                   className="letter-to-heaven-post-details-mobile-nav-item-unique"
                   onClick={() => navigate('/management-fee-payment-inquiry')}
                 >
                   <span>관리비 납부 조회</span>
                   <img 
                     src="/arrows/right.png" 
                     alt="arrow" 
                     className="letter-to-heaven-post-details-mobile-nav-arrow-unique" 
                   />
                 </div>
                <div 
                  className="letter-to-heaven-post-details-mobile-nav-item-unique"
                  onClick={handleAncestralTablet}
                >
                  <span>지방출력</span>
                  <img 
                    src="/arrows/right.png" 
                    alt="arrow" 
                    className="letter-to-heaven-post-details-mobile-nav-arrow-unique" 
                  />
                </div>
              </div>
            </div>
          )}
                 </div>
       </div> */}

        {/* Letter Content Section */}
        <div className="letter-to-heaven-post-details-mobile-content-unique">
          {/* Post Header */}
          <div className="letter-to-heaven-post-details-mobile-post-header-unique">
            <div className="letter-to-heaven-post-details-mobile-recipient-unique">
              To. {letterData ? letterData.recipient : ''}
            </div>
            <div className="letter-to-heaven-post-details-mobile-title-unique">
              {letterData ? letterData.title : ''}
            </div>
            <div className="letter-to-heaven-post-details-mobile-sender-unique">
              From {letterData ? letterData.sender : ''} | {letterData ? formatDate(letterData.date) : ''}
            </div>
          </div>

          {/* Letter Content */}
          {/* <div className="letter-to-heaven-post-details-mobile-letter-content-unique">
            {letterData && letterData.content ? (
              letterData.content.split('\n').map((line, index) => (
                <p key={index}>{line}</p>
              ))
            ) : letterData ? (
              <p>내용이 없습니다.</p>
            ) : null}
          </div> */}

          <div
            className="letter-to-heaven-post-details-mobile-letter-content-unique"
            dangerouslySetInnerHTML={{
              __html: letterData ? normalizeContent(letterData.content) : ''
            }}
          ></div>



          {/* Separator */}
          <div className="letter-to-heaven-post-details-mobile-separator-unique"></div>

          {/* Password Input Section */}
          <div className="letter-to-heaven-post-details-mobile-password-section-unique">
            <label className="letter-to-heaven-post-details-mobile-password-label-unique">비밀번호</label>
            <input
              type="password"
              className="letter-to-heaven-post-details-mobile-password-input-unique"
              placeholder="비밀번호를 입력하세요"
              value={enteredPassword}
              onChange={(e) => handlePasswordChange(e.target.value)}
            />
          </div>

          {/* Action Buttons */}
          <div className="letter-to-heaven-post-details-mobile-action-buttons-unique">
            <button
              className="letter-to-heaven-post-details-mobile-list-button-unique"
              onClick={handleListButton}
            >
              목록
            </button>
            <button
              className="letter-to-heaven-post-details-mobile-modify-button-unique"
              onClick={handleModifyButton}
              disabled={!isPasswordCorrect}
              style={{
                opacity: isPasswordCorrect ? 1 : 0.5,
                cursor: isPasswordCorrect ? 'pointer' : 'not-allowed'
              }}
            >
              수정
            </button>
            <button
              className="letter-to-heaven-post-details-mobile-delete-button-unique"
              onClick={handleDeleteButton}
              disabled={!isPasswordCorrect}
              style={{
                opacity: isPasswordCorrect ? 1 : 0.5,
                cursor: isPasswordCorrect ? 'pointer' : 'not-allowed'
              }}
            >
              삭제
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LetterToHeavenPostDetailsMobile; 